import React from 'react';
import {
  X,
  Edit,
  Trash2
} from 'lucide-react';

function TrackingDetailModal({ item, type, onClose, onEdit, onDelete }) {
  if (!item) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      open: 'bg-blue-100 text-blue-800',
      new: 'bg-blue-100 text-blue-800',
      proposed: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-700',
      investigating: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-purple-100 text-purple-800',
      fixed: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      active: 'bg-green-100 text-green-800',
      implemented: 'bg-green-100 text-green-800',
      validated: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-700',
      rejected: 'bg-gray-100 text-gray-700',
      wont_fix: 'bg-gray-100 text-gray-700',
      deferred: 'bg-gray-100 text-gray-700',
      deprecated: 'bg-gray-100 text-gray-700',
      archived: 'bg-gray-100 text-gray-700',
      invalidated: 'bg-gray-100 text-gray-700',
      regression: 'bg-red-100 text-red-800',
      superseded: 'bg-orange-100 text-orange-800',
      planned: 'bg-indigo-100 text-indigo-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const getSeverityBadgeColor = (severity) => {
    const severityColors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-blue-100 text-blue-800',
      someday: 'bg-gray-100 text-gray-700'
    };
    return severityColors[severity] || 'bg-gray-100 text-gray-700';
  };

  const renderTypeSpecificFields = () => {
    switch (type) {
      case 'bugs':
        return (
          <>
            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Severity</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadgeColor(item.severity)}`}>
                    {item.severity}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-gray-800">{item.category || 'N/A'}</p>
                </div>
              </div>
            </div>

            {item.affected_area && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Affected Area</p>
                <p className="text-sm text-gray-800">{item.affected_area}</p>
              </div>
            )}

            {item.affected_files && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Affected Files</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap font-mono text-xs">{item.affected_files}</p>
              </div>
            )}

            {item.root_cause && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Root Cause</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.root_cause}</p>
              </div>
            )}

            {item.investigation_notes && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Investigation Notes</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.investigation_notes}</p>
              </div>
            )}

            {item.fix_applied && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Fix Applied</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.fix_applied}</p>
              </div>
            )}

            {item.fix_commit && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Fix Commit</p>
                <p className="text-sm text-gray-800 font-mono">{item.fix_commit}</p>
              </div>
            )}

            {item.prevention_notes && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Prevention Notes</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.prevention_notes}</p>
              </div>
            )}

            {item.reported_by && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Reported By</p>
                <p className="text-sm text-gray-800">{item.reported_by}</p>
              </div>
            )}

            {item.resolved_at && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Resolved At</p>
                <p className="text-sm text-gray-800">{formatDate(item.resolved_at)}</p>
              </div>
            )}
          </>
        );

      case 'features':
        return (
          <>
            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-gray-800">{item.category || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(item.status)}`}>
                    {item.status?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 py-4">
              <p className="text-sm text-gray-500 mb-1">Expected Behavior</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.expected_behavior}</p>
            </div>

            {item.api_endpoints && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">API Endpoints</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap font-mono text-xs">{item.api_endpoints}</p>
              </div>
            )}

            {item.frontend_pages && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Frontend Pages</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.frontend_pages}</p>
              </div>
            )}

            {item.key_files && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Key Files</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap font-mono text-xs">{item.key_files}</p>
              </div>
            )}

            {item.dependencies && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Dependencies</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.dependencies}</p>
              </div>
            )}
          </>
        );

      case 'ideas':
        return (
          <>
            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Priority</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadgeColor(item.priority)}`}>
                    {item.priority}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-gray-800">{item.category || 'N/A'}</p>
                </div>
              </div>
            </div>

            {item.area && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Area</p>
                <p className="text-sm text-gray-800">{item.area}</p>
              </div>
            )}

            {item.problem && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Problem</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.problem}</p>
              </div>
            )}

            {item.proposed_solution && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Proposed Solution</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.proposed_solution}</p>
              </div>
            )}

            {item.expected_benefit && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Expected Benefit</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.expected_benefit}</p>
              </div>
            )}

            {item.implementation_notes && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Implementation Notes</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.implementation_notes}</p>
              </div>
            )}

            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {item.effort && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Effort</p>
                    <p className="text-sm text-gray-800">{item.effort}</p>
                  </div>
                )}
                {item.impact && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Impact</p>
                    <p className="text-sm text-gray-800">{item.impact}</p>
                  </div>
                )}
              </div>
            </div>

            {item.tags && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Tags</p>
                <p className="text-sm text-gray-800">{item.tags}</p>
              </div>
            )}

            {item.proposed_by && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Proposed By</p>
                <p className="text-sm text-gray-800">{item.proposed_by}</p>
              </div>
            )}

            {item.implemented_at && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Implemented At</p>
                <p className="text-sm text-gray-800">{formatDate(item.implemented_at)}</p>
              </div>
            )}

            {item.rejection_reason && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Rejection Reason</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.rejection_reason}</p>
              </div>
            )}
          </>
        );

      case 'research':
        return (
          <>
            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-gray-800">{item.category || 'N/A'}</p>
                </div>
                {item.area && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Area</p>
                    <p className="text-sm text-gray-800">{item.area}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 py-4">
              <p className="text-sm text-gray-500 mb-1">Findings</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.findings}</p>
            </div>

            {item.data_evidence && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Data Evidence</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap font-mono text-xs">{item.data_evidence}</p>
              </div>
            )}

            {item.methodology && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Methodology</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.methodology}</p>
              </div>
            )}

            {item.conclusion && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Conclusion</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.conclusion}</p>
              </div>
            )}

            {item.action_taken && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Action Taken</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.action_taken}</p>
              </div>
            )}

            <div className="border-t border-gray-200 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Affects Production</p>
                  <p className="text-sm text-gray-800">{item.affects_production ? 'Yes' : 'No'}</p>
                </div>
                {item.researcher && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Researcher</p>
                    <p className="text-sm text-gray-800">{item.researcher}</p>
                  </div>
                )}
              </div>
            </div>

            {item.tags && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Tags</p>
                <p className="text-sm text-gray-800">{item.tags}</p>
              </div>
            )}

            {item.research_date && (
              <div className="border-t border-gray-200 py-4">
                <p className="text-sm text-gray-500 mb-1">Research Date</p>
                <p className="text-sm text-gray-800">{formatDate(item.research_date)}</p>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-800 to-red-900 text-white px-6 py-4 rounded-t-lg flex items-center justify-between sticky top-0">
          <h2 className="text-xl font-bold">
            {type.charAt(0).toUpperCase() + type.slice(1, -1)} #{item.id}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Title/Name */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-1">{type === 'features' ? 'Name' : 'Title'}</p>
            <h3 className="text-xl font-semibold text-gray-800">{item.name || item.title}</h3>
          </div>

          {/* Status */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-1">Status</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(item.status)}`}>
              {item.status?.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Description/Summary */}
          <div className="border-t border-gray-200 py-4">
            <p className="text-sm text-gray-500 mb-1">{type === 'ideas' || type === 'research' ? 'Summary' : 'Description'}</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.description || item.summary}</p>
          </div>

          {/* Type-specific fields */}
          {renderTypeSpecificFields()}

          {/* Timestamps */}
          <div className="border-t border-gray-200 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Created</p>
                <p className="text-sm text-gray-800">{formatDate(item.created_at)}</p>
              </div>
              {item.updated_at && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Updated</p>
                  <p className="text-sm text-gray-800">{formatDate(item.updated_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-lg">
          <button
            onClick={() => onEdit(item)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
          <button
            onClick={() => onDelete(item)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrackingDetailModal;
